/**
 * Reports V2 Page
 *
 * Clean, simplified reports page with only time range selection
 * No actual report implementation yet - just the foundation
 */

import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { PeriodSelectorBar } from "../components/period-selector-bar";
import { ReportsLimitationBanner } from "../components/reports-limitation-banner";
import { ReportsTabs } from "../components/reports-tabs";
import { useOperationsData } from "../hooks/use-operations-data";
import { useOverviewData } from "../hooks/use-overview-data";
import { useReportsTools } from "../hooks/use-reports-tools";
import { useReportsViewContext } from "../hooks/use-reports-view-context";
import { useWebData } from "../hooks/use-web-data";
import { useReportsV2Store } from "../store/use-reports-v2-store";

export function ReportsV2Page() {
  // Register reports tools with assistant-ui
  useReportsTools();
  const { t } = useTranslation("reports");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { currentPeriod, comparisonPeriod } = useReportsV2Store();
  const { data: overviewData, loading: overviewLoading, error: overviewError } = useOverviewData();
  const {
    data: operationsData,
    loading: operationsLoading,
    error: operationsError,
  } = useOperationsData();
  const { data: webData, loading: webLoading, error: webError } = useWebData();

  const loading = overviewLoading || operationsLoading || webLoading;
  const error = overviewError || operationsError || webError;

  // Check if user has limited (basic tier) access
  // If any premium metric is undefined, user is on basic tier
  const isLimitedAccess = overviewData ? !overviewData.financial.averageTransactionValue : false;

  // Register view context for AI assistant
  useReportsViewContext({
    key: "reports",
    currentPeriod,
    comparisonPeriod,
    overviewData,
    operationsData,
    webData,
    loading,
    isLimitedAccess,
  });

  return (
    <>
      <BaseNavigationBar
        title={t("pageTitle")}
        subtitle={t("pageSubtitle")}
        leadElement={
          isMobile ? <BackButton key="back-button" onBack={() => navigate(-1)} /> : undefined
        }
        bottomElement={<PeriodSelectorBar />}
      />

      <PageContent>
        {/* Premium Limitation Banner */}
        {!loading && !error && <ReportsLimitationBanner isLimited={isLimitedAccess} />}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
              <p className="text-muted-foreground">{t("loading")}</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t("error")}</AlertDescription>
          </Alert>
        )}

        {/* Data Display */}
        {!loading && !error && overviewData && (
          <ReportsTabs
            overviewData={overviewData}
            operationsData={operationsData}
            webData={webData}
          />
        )}
      </PageContent>
    </>
  );
}
